import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Column } from 'react-table';
import { Container, Flex, Grid, GridItem, Text, useDisclosure } from '@chakra-ui/react';
import { WarningOutlined } from '@ant-design/icons';
import Lottie from 'lottie-react';
import LorealStatsPage from '@/customSlides/loreal/LorealStatsPage/LorealStatsPage';
import BestAsset from '@/features/meeting-stats/components/BestAsset';
import BestSlide from '@/features/meeting-stats/components/BestSlide';
import { ChunkContainerLoading } from '@/features/meeting-stats/components/ChunkContainer';
import EngagementContributors from '@/features/meeting-stats/components/EngagementContributors';
import { ParticipantChunk } from '@/features/meeting-stats/components/ParticipantChunk';
import PercentageChunk from '@/features/meeting-stats/components/PercentageChunk';
import ViewedCase from '@/features/meeting-stats/components/ViewedCase';
import { MeetingStatsViewContext } from '@/features/meeting-stats/context/MeetingStatsViewContext';
import { ParsedAssetViews } from '@/features/meeting-stats/utils/parseAssetViewsPerCase';
import { ParsedSlideStats } from '@/features/meeting-stats/utils/parseSlideStatsPerCase';
import { useUserAuthenticationContext } from '@/features/userAuth/context/UserAuthenticationContext';
import useDeviceSize from '@/services/hooks/useDeviceSize';
import PageLayout from '@/ui/Layout/PageLayout';
import { captureSentry } from '@/utils/helpers/sentryHelper';
import Footer from '../Footer';
import Header from '../Header';
import SpecificCaseStatsView from '../SpecificCaseStatsView/SpecificCaseStatsView';
import npsScoreNoVoteAnimationData from './npsScoreNoVotesAnimation.lottie.json';
import ScoreDetailModal from './ScoreDetailModal';

export type MeetingLLXNPSRow = {
  userID: string;
  scores: number[];
};

export type MeetingLLXScoreDetailsRow = {
  case: string;
  score: number;
};

export const MeetingStatsView = () => {
  const { isDesktopOrLaptop } = useDeviceSize();
  const { t } = useTranslation();
  const {
    isOpen: isEngagementScoreDetailOpen,
    // onOpen: onEngagementScoreDetailOpen,
    onClose: onEngagementScoreDetailClose,
  } = useDisclosure();
  const {
    isOpen: isNPSDetailsOpen,
    onOpen: onNPSDetailsOpen,
    onClose: onNPSDetailsClose,
  } = useDisclosure();
  const {
    caseIds,
    caseTitles,
    currentCase,
    parsedSlideStats,
    parsedAssetViews,
    // engagementScore,
    caseIdToLLXRating,
    npsScore,
    amtNpsScores,
    scoresPerUser,
    meetingDate,
    meetingLocation,
    totalDuration,
    longestCaseDuration,
    consensusRate,
    uniqueCasesPresented,
    answerList,
  } = useContext(MeetingStatsViewContext);

  const isConsensusRateDisabled = answerList.length === 0;

  const { isAdmin } = useUserAuthenticationContext();

  const isLorealCase = currentCase === 'lorealFR' || currentCase === 'lorealNL';

  const caseToTop4Slides = useMemo(() => {
    if (!parsedSlideStats) return undefined;
    const toReturn: Record<string, ParsedSlideStats[]> = {};
    caseIds.forEach((caseId) => {
      const slides = parsedSlideStats[caseId];
      toReturn[caseId] = slides.slice(0, 4);
    });
    return toReturn;
  }, [parsedSlideStats, caseIds]);

  const bestAssetPerCase = useMemo(() => {
    if (!parsedAssetViews) return undefined;
    return caseIds.reduce((acc, curr) => {
      acc.set(curr, parsedAssetViews[curr][0]);
      return acc;
    }, new Map<string, ParsedAssetViews>());
  }, [parsedAssetViews, caseIds]);

  const bestAssetInGeneral = useMemo(() => {
    if (!bestAssetPerCase) return undefined;
    let record:
      | {
          caseTitle: string;
          asset: ParsedAssetViews | undefined;
        }
      | undefined = undefined;
    for (const [key, asset] of bestAssetPerCase.entries()) {
      if (!record || !record.asset || !asset || asset.views > record.asset.views) {
        record = {
          caseTitle: caseTitles[key],
          asset,
        };
      }
    }
    return record;
  }, [bestAssetPerCase]);

  const llxNPSColumns: Column<MeetingLLXNPSRow>[] = [
    {
      Header: 'User ID',
      accessor: 'userID',
    },
    {
      Header: 'Scores',
      accessor: (row) => `${row.scores.join(', ')}`,
    },
  ];
  const llxNPSData = (() => {
    if (scoresPerUser.size === 0) {
      return [
        {
          userID: 'No data',
          scores: [],
        },
      ];
    }
    return Array.from(scoresPerUser).map((elem) => {
      return {
        userID: elem[1][0].user.email,
        scores: elem[1].flatMap((el) => el.scores) || [],
      };
    });
  })();

  const llxNpsWarning = (() => {
    if (scoresPerUser.size === 0) {
      return false;
    }
    return Array.from(scoresPerUser).some((elem) => {
      return elem[1].length > 1;
    });
  })();

  const llxScoreDetailsColumns: Column<MeetingLLXScoreDetailsRow>[] = [
    {
      Header: 'Case',
      accessor: 'case',
    },
    {
      Header: 'Score',
      accessor: (row) => `${(row.score * 100).toFixed(2)}%`,
    },
  ];

  const llxScoreDetailsData = useMemo(() => {
    if (Object.keys(caseTitles).length === 0) {
      return [
        {
          case: 'Loading',
          score: 0,
        },
      ];
    }
    if (uniqueCasesPresented.length === 0) {
      return [
        {
          case: 'No cases presented',
          score: 0,
        },
      ];
    }
    const result = uniqueCasesPresented.map((elem) => {
      const title = caseTitles[elem];

      if (!title) {
        captureSentry({
          title: 'Case title not found',
          detail: {
            caseId: elem,
            caseTitles,
            uniqueCasesPresented: uniqueCasesPresented,
          },
        });
      }
      return {
        case: caseTitles[elem] || 'Not available',
        score: caseIdToLLXRating.get(elem) || 0,
      };
    });

    return result;
  }, [uniqueCasesPresented, caseTitles]);

  return (
    <PageLayout
      showHeader={false}
      showFooter={false}
      background="linear-gradient(to bottom right, #F9FAFF, #ECF0FF)"
    >
      <Header date={meetingDate} location={meetingLocation} />

      <Container
        padding="20px"
        style={{
          minWidth: '50%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Grid
          templateColumns={`repeat(${isDesktopOrLaptop ? 2 : 1}, 1fr)`}
          templateRows="auto"
          rowGap={isDesktopOrLaptop ? '22px' : '15px'}
          columnGap="22px"
        >
          {currentCase === '' && (
            <>
              {/* <GridItem>
                {engagementScore !== undefined ? (
                  <PercentageChunk
                    percentage={Math.floor(engagementScore * 100)}
                    title={t('meetings.stats.engagement.title')}
                    onClick={
                      isAdmin ? () => onEngagementScoreDetailOpen() : undefined
                    }
                  />
                ) : (
                  <ChunkContainerLoading />
                )}
              </GridItem> */}
              <GridItem colSpan={isDesktopOrLaptop ? 1 : 1} display="flex">
                {npsScore !== undefined ? (
                  <PercentageChunk
                    percentage={Math.floor(npsScore * 100)}
                    title={t('meetings.stats.nps.title')}
                    onClick={isAdmin ? () => onNPSDetailsOpen() : undefined}
                    loading={amtNpsScores === 0}
                    LoadingAnimation={
                      <Lottie
                        animationData={npsScoreNoVoteAnimationData}
                        data-test="image-loading-animation"
                        loop={true}
                        style={{
                          width: '20%',
                          height: '100%',
                          marginBottom: '12px',
                        }}
                      />
                    }
                  />
                ) : (
                  <ChunkContainerLoading />
                )}
              </GridItem>
              <GridItem>
                <ParticipantChunk />
              </GridItem>
              <GridItem>
                <ViewedCase />
              </GridItem>
              <GridItem>
                {caseIds && caseToTop4Slides ? (
                  <BestSlide
                    caseIds={caseIds}
                    caseTitles={caseTitles}
                    caseToTop4Slides={caseToTop4Slides}
                  />
                ) : (
                  <ChunkContainerLoading />
                )}
              </GridItem>
              <GridItem>
                {bestAssetInGeneral ? (
                  <BestAsset
                    caseTitle={bestAssetInGeneral.caseTitle}
                    asset={bestAssetInGeneral.asset}
                  />
                ) : (
                  <ChunkContainerLoading />
                )}
              </GridItem>
              <GridItem>
                <EngagementContributors
                  options={[
                    {
                      title: t('meetings.stats.engagementContributors.totalDuration.title'),
                      value: Math.ceil(totalDuration),
                      maxValue: Math.ceil(totalDuration),
                      textOnTheRight: `${Math.ceil(totalDuration)} min.`,
                    },
                    {
                      title: t('meetings.stats.engagementContributors.longestCase.title'),
                      value: Math.ceil(longestCaseDuration),
                      maxValue: Math.ceil(totalDuration),
                      textOnTheRight: `${Math.ceil(longestCaseDuration)} min.`,
                    },
                    {
                      title: t('meetings.stats.engagementContributors.consensusRate.title'),
                      value: Math.floor(consensusRate * 100),
                      maxValue: 100,
                      textOnTheRight: `${Math.floor(consensusRate * 100)}%`,
                      disabled: isConsensusRateDisabled,
                      disabledTooltip:
                        t('meetings.stats.engagementContributors.consensusRate.disabled.tooltip') ??
                        undefined,
                    },
                  ]}
                />
              </GridItem>
              <GridItem colSpan={isDesktopOrLaptop ? 2 : 1}>
                <Footer />
              </GridItem>
            </>
          )}
          <ScoreDetailModal
            isOpen={isEngagementScoreDetailOpen}
            onClose={onEngagementScoreDetailClose}
            columns={llxScoreDetailsColumns}
            data={llxScoreDetailsData}
          />
          <ScoreDetailModal
            isOpen={isNPSDetailsOpen}
            onClose={onNPSDetailsClose}
            columns={llxNPSColumns}
            data={llxNPSData}
          >
            {llxNpsWarning && (
              <Flex w="full" borderRadius={4} px={4} py={2} bg="#E9D502" gap={2}>
                <WarningOutlined style={{ paddingTop: '2px' }} />
                <Text>{t('meetings.stats.nps.warning')}</Text>
              </Flex>
            )}
          </ScoreDetailModal>
          {currentCase !== '' && !isLorealCase && (
            <SpecificCaseStatsView
              caseToTop4Slides={caseToTop4Slides}
              bestAssetPerCase={bestAssetPerCase}
            />
          )}
        </Grid>
      </Container>

      {currentCase !== '' && isLorealCase && <LorealStatsPage />}
    </PageLayout>
  );
};
